/**
 * PermissionsProvider + useCurrentPermissions + useHasPermission.
 *
 * Reactive replacement for the JWT-decode-at-login permissions read.
 *
 * BEHAVIOR
 * --------
 *   • On mount fetches `GET /api/me/permissions` (via API_URL, same
 *     header + 401 handling as the rest of the app).
 *   • Refetches on:
 *       – window focus / tab visibilitychange
 *       – route change (useLocation)
 *       – every 60 s while mounted
 *       – imperative refetch() the provider exposes
 *   • Mirrors the latest payload into a module-level snapshot
 *     (`permissionsCache`) so the legacy plain
 *     `hasPermission(currentUser, [perm])` call-sites automatically
 *     benefit from the freshest server-side data without a signature
 *     change. See permissionsCache.js for the reasoning.
 *
 * BACKWARD COMPAT
 * ---------------
 *   If the endpoint 404s (coord not yet deployed with the SCK-547
 *   counterpart), the provider LOGS A WARN and populates the cache
 *   with the JWT-derived permission list so gates keep working. Same
 *   behavior as before this change. Other errors are exposed via the
 *   hook's `error` field but do NOT clear the cache.
 *
 * WHY NOT WRAP APP.JS ROOT
 * ------------------------
 *   The provider uses `useLocation()` to refetch on route change,
 *   which requires being inside RouterProvider. It is mounted inside
 *   `ProtectedRoutes` so it wraps the entire authenticated app tree
 *   in one instance. Nested `ProtectedRoutes` re-mount the wrapper
 *   too, but a sentinel on the default context value lets nested
 *   instances no-op instead of double-spawning pollers.
 *
 * SCK-547.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { API_URL, ROLE_PERMISSIONS, USER_TYPE_SYSTEM } from "../helpers/constants";
import { getToken, getUserData } from "../helpers/tokenStorage";
import {
  clearPermissionsSnapshot,
  setPermissionsSnapshot,
} from "./permissionsCache";

const REFRESH_INTERVAL_MS = 60 * 1000;

// The default value's `__default: true` sentinel lets nested
// <PermissionsProvider>s detect that they're the outermost mount and
// avoid double-spawning pollers when a parent already provides.
const PermissionsContext = createContext({
  __default: true,
  permissions: [],
  projects: [],
  accountRole: null,
  source: null,
  isLoading: false,
  error: null,
  refetch: () => {},
});

/**
 * Derive the legacy JWT-based permissions list (the union of every
 * verb in ROLE_PERMISSIONS for each of the user's role memberships)
 * so the fallback path produces the same string[] shape as the API
 * response. Used only when the coord endpoint isn't available yet.
 */
const jwtFallbackPermissions = (user) => {
  const perms = new Set();
  if (!user) return [];
  if (user.userType === USER_TYPE_SYSTEM) {
    perms.add("SYSTEM");
  }
  for (const group of user.groups || []) {
    const roleType = group?.userRole?.roleType;
    if (roleType) perms.add(roleType);
    (ROLE_PERMISSIONS[roleType] || []).forEach((p) => perms.add(p));
  }
  return Array.from(perms);
};

const PermissionsProviderInner = ({ children }) => {
  const [state, setState] = useState({
    permissions: [],
    projects: [],
    accountRole: null,
    source: null,
    isLoading: false,
    error: null,
  });

  // Guard against setState-after-unmount when a fetch races the
  // component tearing down (logout, hot reload).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const location = useLocation();

  const doFetch = useCallback(async () => {
    const token = getToken();
    if (!token) {
      // Signed out — clear the cache so a stale value from a previous
      // session can't leak into an unauthenticated render.
      clearPermissionsSnapshot();
      if (mounted.current) {
        setState((prev) => ({
          ...prev,
          permissions: [],
          projects: [],
          accountRole: null,
          source: null,
          isLoading: false,
          error: null,
        }));
      }
      return;
    }

    if (mounted.current) {
      setState((prev) => ({ ...prev, isLoading: true }));
    }

    try {
      const res = await fetch(`${API_URL}/me/permissions`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 404) {
        // Coord hasn't been deployed with the SCK-547 endpoint yet.
        // Fall back to the JWT-derived permission list so button
        // gates keep working — same behavior as before this change.
        console.warn(
          "[PermissionsProvider] /api/me/permissions returned 404 — " +
            "falling back to JWT-decoded permissions. This is expected " +
            "during a partial deploy; once the coord counterpart lands " +
            "this warn will stop firing."
        );
        const jwtPerms = jwtFallbackPermissions(getUserData());
        setPermissionsSnapshot({
          source: "jwt",
          permissions: jwtPerms,
          projects: [],
          accountRole: null,
        });
        if (mounted.current) {
          setState({
            permissions: jwtPerms,
            projects: [],
            accountRole: null,
            source: "jwt",
            isLoading: false,
            error: null,
          });
        }
        return;
      }

      if (!res.ok) {
        throw new Error(
          `Permissions fetch failed: ${res.status} ${res.statusText}`
        );
      }

      const payload = await res.json();
      const permissions = Array.isArray(payload?.permissions)
        ? [...payload.permissions]
        : [];
      const projects = Array.isArray(payload?.projects) ? payload.projects : [];
      const accountRole = payload?.accountRole ?? null;

      // Defense-in-depth: legacy call-sites still gate on the string
      // "SYSTEM" appearing in permissions[] (see hasPermission +
      // useHasPermission wildcard short-circuit). The SCK-547 emitter
      // is entitlement-first and doesn't always echo that synthetic
      // verb, so a SYSTEM caller was rendering as ACCOUNT_ADMIN in
      // gate-driven code. Inject it here whenever the caller's userType
      // (from the API payload OR the JWT-decoded local user) is SYSTEM,
      // so the two code paths agree.
      const payloadUserType = payload?.userType || payload?.user_type;
      const jwtUserType = getUserData()?.userType;
      const isSystemUser =
        payloadUserType === USER_TYPE_SYSTEM ||
        jwtUserType === USER_TYPE_SYSTEM;
      if (isSystemUser && !permissions.includes("SYSTEM")) {
        permissions.push("SYSTEM");
      }

      setPermissionsSnapshot({
        source: "api",
        permissions,
        projects,
        accountRole,
      });

      if (mounted.current) {
        setState({
          permissions,
          projects,
          accountRole,
          source: "api",
          isLoading: false,
          error: null,
        });
      }
    } catch (err) {
      // Transient error — DON'T clear the cache; leave whatever the
      // last successful fetch put there so gates don't flicker off.
      console.error("[PermissionsProvider] fetch error:", err);
      if (mounted.current) {
        setState((prev) => ({ ...prev, isLoading: false, error: err }));
      }
    }
  }, []);

  // Initial fetch on mount + route change.
  useEffect(() => {
    doFetch();
  }, [doFetch, location.pathname]);

  // Refetch on window focus / visibilitychange.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        doFetch();
      }
    };
    const onFocus = () => doFetch();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [doFetch]);

  // Refetch every REFRESH_INTERVAL_MS.
  useEffect(() => {
    const id = setInterval(() => {
      doFetch();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [doFetch]);

  const value = useMemo(
    () => ({
      ...state,
      refetch: doFetch,
    }),
    [state, doFetch]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const PermissionsProvider = ({ children }) => {
  // If a parent already provides, just pass through — no duplicate
  // fetcher, no duplicate 60s poller. Detected via the `__default`
  // sentinel on the un-provided context value.
  const parent = useContext(PermissionsContext);
  if (!parent.__default) {
    return <>{children}</>;
  }
  return <PermissionsProviderInner>{children}</PermissionsProviderInner>;
};

/**
 * Full permissions payload + refetch handle. Prefer `useHasPermission`
 * when all you need is a boolean gate.
 */
export const useCurrentPermissions = () => useContext(PermissionsContext);

/**
 * Reactive boolean gate. Pass a single verb string or an array of
 * verbs (matches if ANY are present). SYSTEM users match everything,
 * matching the historical hasPermission semantics.
 */
export const useHasPermission = (required) => {
  const { permissions } = useContext(PermissionsContext);
  const wanted = Array.isArray(required) ? required : [required];
  if (permissions?.includes("SYSTEM")) return true;
  return wanted.some((verb) => permissions?.includes(verb));
};

/**
 * Reactive gate for "is this caller a platform super-user (SYSTEM) or
 * an equivalent cross-tenant admin?" — the correct predicate for
 * choosing between the cross-tenant Directory surface and the
 * single-account Members surface.
 *
 * Historically the two Directory surfaces both derived `isSystem` by
 * checking `permissions.includes("SYSTEM")`. That string is a
 * SYNTHETIC verb the JWT-fallback path adds, not something the
 * SCK-547 `/api/me/permissions` endpoint promises to echo — so once
 * the endpoint was live, SYSTEM callers started rendering as
 * ACCOUNT_ADMIN (Members label, no Accounts sub-tab, DirectoryUsers
 * mounted AccessMembers instead of the cross-tenant table).
 *
 * The correct source of truth for "am I SYSTEM userType?" is the JWT-
 * decoded local user; the entitlement `directory.account.manage` is
 * kept as an OR clause for delegated cross-tenant admins who aren't
 * SYSTEM userType but still need the cross-tenant surface.
 *
 * SYSTEM in the reactive permissions snapshot also counts (matches
 * useHasPermission's wildcard behavior) so a caller whose JWT is
 * stale but whose live snapshot has SYSTEM still flips true.
 */
export const useIsSystem = () => {
  const { permissions } = useContext(PermissionsContext);
  const user = getUserData();
  if (user?.userType === USER_TYPE_SYSTEM) return true;
  if (permissions?.includes("SYSTEM")) return true;
  if (permissions?.includes("directory.account.manage")) return true;
  return false;
};
